import React, { useState, useEffect, useRef } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  Button,
  Typography,
  Row,
  Col,
  Modal,
  notification,
  Result,
  Tooltip,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

import classnames from 'classnames';

import { WithLoading } from 'common/components';

import { CustomizeTransfer } from 'common/components';
import ExportPropertiesTree from './ExportPropertiesTree';
import ExportProductToolbar from './ExportProductToolbar';

import * as productServices from 'services/product';
import { useGetProductPropertiesToExport } from 'hooks/exportProductPropertiesHooks';
import { useGlobalModal } from 'hooks';

import * as api from 'config/axios';
import * as globalSelectors from '@redux/global/selectors';

import Messages from 'i18n/messages/product';

import './BulkEditProduct.less';

const { Title } = Typography;

const ExportProductPropertiesModal = (props) => {
  const intl = useIntl();
  const addedKeysRef = useRef([]);

  const { onExport, title, visible, loading, closeModal } = props;

  const [leftExpandedKeys, setLeftExpandedKeys] = useState([]);
  const [isShowSelection, setIsShowSelection] = useState(false);
  const searchHook = useState('');
  const [searchText, setSearchText] = searchHook;

  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [isExportProperties, setIsExportProperties] = useState(false);
  const [isPopulated, setIsPopulated] = useState(false);

  const { sourceData, rootData, loadingStatus } =
    useGetProductPropertiesToExport(visible);

  const upDownProgressData = useSelector(
    globalSelectors.selectUploadDownloadProgress()
  );

  const onOk = () => {
    setIsExportProperties(true);
    const exportProperties = !isPopulated
      ? getExportedPropertyFieldName({
          data: sourceData,
          addedKeys: addedKeysRef.current,
        })
      : [];
    onExport && onExport(exportProperties, isPopulated);
  };

  const closeExportModal = () => {
    if (!isExportProperties) return;

    const isDownLoadSuccess = upDownProgressData
      ?.filter((item) => item?.type === 'download')
      ?.every((item) => item?.status === 'done');

    if (isDownLoadSuccess) {
      closeModal && closeModal();
      setIsExportProperties(false);
    }
  };

  useEffect(() => {
    closeExportModal();
  }, [upDownProgressData]);

  const onCancel = () => {
    closeModal && closeModal();
  };

  const onClickDownloadManual = () => {
    callApiDownloadManual();
  };

  const onTransferChange = (addedKeys) => {
    addedKeysRef.current = addedKeys;
    setSubmitDisabled(!addedKeys.length);
  };

  const callApiDownloadManual = () => {
    productServices
      .downloadManualEditingBulkProduct()
      .then((response) => {
        const { isSuccess, data } = response;
        if (isSuccess && data) api.sendDownload({ url: response?.data?.url });
        else handleDownloadManualError(response);
      })
      .catch(() => {
        handleDownloadManualError();
      });
  };

  const handleDownloadManualError = (response) => {
    notification.error({
      message:
        response?.message ||
        response?.data?.message ||
        intl.formatMessage(Messages.exportPropertiesDownloadManualError),
    });
  };

  const getExportedPropertyFieldName = ({ data, addedKeys }) => {
    return addedKeys.reduce((accumulator, currentAddedKeys) => {
      const addedPropertyData = data.find((propertyItem) => {
        return propertyItem.key === currentAddedKeys;
      });
      const fieldFullPath = addedPropertyData?.data?.fieldFullPath;

      if (addedPropertyData && fieldFullPath) accumulator.push(fieldFullPath);

      return accumulator;
    }, []);
  };

  const resetState = () => {
    if (visible) return;

    setIsShowSelection(false);
    setLeftExpandedKeys([]);
    setSearchText('');
  };

  useEffect(() => {
    resetState();
  }, [visible]);

  const renderModalTitle = () => {
    const DownloadManualBtn = Button;

    return (
      <Row style={{ paddingRight: 30 }}>
        <Col flex={0}>
          <Title level={5} style={{ marginBottom: 0 }}>
            {title}
          </Title>
        </Col>
        <Col flex={1}></Col>
        <Col flex={0}>
          <Tooltip
            title={intl.formatMessage(
              Messages.exportPropertiesDownloadManualTitleToolTip
            )}
          >
            <DownloadManualBtn
              type='text'
              icon={<QuestionCircleOutlined />}
              onClick={onClickDownloadManual}
            />
          </Tooltip>
        </Col>
      </Row>
    );
  };

  return (
    <Modal
      title={renderModalTitle()}
      visible={visible}
      bodyStyle={{
        height: '80vh',
        maxHeight: 600,
      }}
      onOk={onOk}
      okButtonText={intl.formatMessage(Messages.exportPropertiesSubmit)}
      okButtonProps={{
        loading: loading,
        disabled: submitDisabled && !isPopulated,
      }}
      onCancel={onCancel}
      width={1300}
      destroyOnClose
      centered
    >
      <ExportProductToolbar
        visible={visible}
        dataSource={sourceData}
        leftExpandedKeys={leftExpandedKeys}
        setLeftExpandedKeys={setLeftExpandedKeys}
        isShowSelection={isShowSelection}
        setIsShowSelection={setIsShowSelection}
        searchHook={searchHook}
        isPopulated={isPopulated}
        setIsPopulated={setIsPopulated}
        submitDisabled={submitDisabled}
      />
      {loadingStatus === 'loading' ? (
        <div className='export-properties__loading-container'>
          <WithLoading
            loading
            className='export-properties__loading-spinning'
          />
        </div>
      ) : null}
      {loadingStatus === 'error' ? (
        <div className='export-properties__error-container'>
          <Result
            status='error'
            title={intl.formatMessage(Messages.exportPropertiesGetDataError)}
            className='export-properties__error-result'
          />
        </div>
      ) : null}
      {loadingStatus === 'success' ? (
        <div
          className={classnames('export-properties__transfer-container', {
            'export-properties__pointer-none': isPopulated,
          })}
        >
          <div
            className={classnames('export-properties__transfer-overlay', {
              'export-properties__transfer-overlay--show': isPopulated,
            })}
          >
            <Title style={{ textShadow: '1px 1px #25252578' }} level={5} strong>
              All properties are populated!
            </Title>
          </div>
          <CustomizeTransfer
            visible={visible}
            dataSource={sourceData}
            leftExpandedKeys={leftExpandedKeys}
            setLeftExpandedKeys={setLeftExpandedKeys}
            isShowSelection={isShowSelection}
            searchText={searchText}
            onChange={onTransferChange}
          >
            {(listProps) => (
              <ExportPropertiesTree
                {...listProps}
                propertyTitle='exportProperties'
                rootData={rootData}
              />
            )}
          </CustomizeTransfer>
        </div>
      ) : null}
    </Modal>
  );
};

export default ExportProductPropertiesModal;
